/**
 * 通知 dispatch 詳情（AC-H-6 / AC-H-8）。
 *
 * 對應 BE GET /admin/notifications/:id — 回 { dispatch, attempts }。
 * `dlq_entries` 為 forward-compat 預留欄位（BE 目前未回；UI 有資料才顯示）。
 */

import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from 'react-router-dom';

import {
  getNotificationDispatch,
  type DispatchDetail,
  type NotificationAttempt,
  type NotificationAttemptStatus,
  type NotificationChannelName,
  type NotificationDlqEntry,
  type NotificationSeverity,
} from '../lib/api-client';

const CHANNEL_LABEL: Record<NotificationChannelName, string> = {
  webhook: 'Webhook',
  sms: 'SMS',
  email: 'Email',
  line: 'LINE',
};

const STATUS_LABEL: Record<NotificationAttemptStatus, { label: string; cls: string }> = {
  pending: { label: '等待中', cls: 'badge warning' },
  sent: { label: '已送出', cls: 'badge success' },
  failed: { label: '失敗', cls: 'badge danger' },
  acked: { label: '已確認', cls: 'badge success' },
};

const SEVERITY_STYLE: Record<NotificationSeverity, { bg: string; color: string }> = {
  P1: { bg: '#fee2e2', color: '#b91c1c' },
  P2: { bg: '#ffedd5', color: '#c2410c' },
  P3: { bg: '#fef3c7', color: '#92400e' },
  INFO: { bg: '#e5e7eb', color: '#374151' },
};

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('zh-TW');
  } catch {
    return iso;
  }
}

function SeverityBadge({ severity }: { severity: string | null | undefined }): JSX.Element {
  const known = (['P1', 'P2', 'P3', 'INFO'] as const).find((v) => v === severity);
  if (!known) {
    return <span className="badge">{severity ?? '—'}</span>;
  }
  const s = SEVERITY_STYLE[known];
  return (
    <span className="badge" style={{ background: s.bg, color: s.color, fontWeight: 600 }}>
      {known}
    </span>
  );
}

function PayloadView({ payload }: { payload: unknown }): JSX.Element {
  let json: string;
  try {
    json = JSON.stringify(payload, null, 2);
  } catch {
    json = String(payload);
  }
  return (
    <pre
      style={{
        margin: 0,
        padding: 12,
        background: '#f9fafb',
        border: '1px solid #e5e7eb',
        borderRadius: 4,
        fontSize: 12,
        lineHeight: 1.5,
        overflow: 'auto',
        maxHeight: 360,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
      }}
    >
      {json}
    </pre>
  );
}

function StatusBadge({ status }: { status: NotificationAttemptStatus }): JSX.Element {
  const s = STATUS_LABEL[status];
  return <span className={s.cls}>{s.label}</span>;
}

function DispatchHeader({ detail }: { detail: DispatchDetail }): JSX.Element {
  const d = detail.dispatch;
  return (
    <section
      style={{
        padding: 16,
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: 4,
        margin: '12px 0',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 16,
        }}
      >
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Dispatch ID</div>
          <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>{d.id}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>等級</div>
          <div style={{ marginTop: 2 }}>
            <SeverityBadge severity={d.severity} />
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>類型 kind</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{d.kind}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>建立時間</div>
          <div style={{ fontSize: 13 }}>{formatDateTime(d.created_at)}</div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>送達時間</div>
          <div style={{ fontSize: 13 }}>
            {d.delivered_at ? (
              formatDateTime(d.delivered_at)
            ) : (
              <span className="badge warning">尚未送達</span>
            )}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>送達通道</div>
          <div style={{ fontSize: 13 }}>
            {d.delivered_via ? (
              <span className="badge success">{CHANNEL_LABEL[d.delivered_via]}</span>
            ) : (
              '—'
            )}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>Payload</div>
        <PayloadView payload={d.payload} />
      </div>
    </section>
  );
}

function AttemptsTable({ attempts }: { attempts: NotificationAttempt[] }): JSX.Element {
  return (
    <section style={{ margin: '12px 0' }}>
      <h2 style={{ fontSize: 16, margin: '0 0 8px 0' }}>Attempts（{attempts.length}）</h2>
      <table>
        <thead>
          <tr>
            <th>通道</th>
            <th style={{ textAlign: 'right' }}>嘗試次數</th>
            <th>狀態</th>
            <th>送出時間</th>
            <th>確認時間</th>
            <th>錯誤訊息</th>
          </tr>
        </thead>
        <tbody>
          {attempts.length === 0 && (
            <tr>
              <td colSpan={6} style={{ textAlign: 'center', color: '#6b7280' }}>
                此 dispatch 尚無 attempt 記錄
              </td>
            </tr>
          )}
          {attempts.map((a) => (
            <tr key={a.id}>
              <td>
                <span className="badge">{CHANNEL_LABEL[a.channel]}</span>
              </td>
              <td style={{ textAlign: 'right' }}>{a.attempt_number}</td>
              <td>
                <StatusBadge status={a.status} />
              </td>
              <td style={{ fontSize: 12, color: '#6b7280' }}>{formatDateTime(a.sent_at)}</td>
              <td style={{ fontSize: 12, color: '#6b7280' }}>{formatDateTime(a.acked_at)}</td>
              <td
                style={{ fontSize: 12, color: '#6b7280', maxWidth: 320 }}
                title={a.error_message ?? ''}
              >
                {a.error_message ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function DlqEntriesTable({ entries }: { entries: NotificationDlqEntry[] }): JSX.Element {
  return (
    <section style={{ margin: '12px 0' }}>
      <h2 style={{ fontSize: 16, margin: '0 0 8px 0' }}>DLQ 項目（{entries.length}）</h2>
      <table>
        <thead>
          <tr>
            <th>通道</th>
            <th style={{ textAlign: 'right' }}>重試</th>
            <th>下次重試</th>
            <th>進入 DLQ</th>
            <th>最後錯誤</th>
            <th>狀態</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e) => (
            <tr key={e.id}>
              <td>
                <span className="badge">{CHANNEL_LABEL[e.channel]}</span>
              </td>
              <td style={{ textAlign: 'right' }}>{e.retry_count}</td>
              <td style={{ fontSize: 12, color: '#6b7280' }}>{formatDateTime(e.next_retry_at)}</td>
              <td style={{ fontSize: 12, color: '#6b7280' }}>
                {formatDateTime(e.moved_to_dlq_at)}
              </td>
              <td
                style={{ fontSize: 12, color: '#6b7280', maxWidth: 320 }}
                title={e.last_error ?? ''}
              >
                {e.last_error ?? '—'}
              </td>
              <td>
                {e.resolved_at ? (
                  <span className="badge success">已 resolve</span>
                ) : (
                  <span className="badge warning">待處理</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

export function NotificationDispatchDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const dispatchId = id ?? '';

  const detailQuery = useQuery({
    queryKey: ['notifications.dispatch', dispatchId],
    queryFn: () => getNotificationDispatch(dispatchId),
    enabled: dispatchId.length > 0,
  });

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">通知 Dispatch 詳情</h1>
        <div className="page-head__actions">
          <Link to="/notifications/dlq">
            <button>← 回 DLQ 列表</button>
          </Link>
          <button onClick={() => detailQuery.refetch()} disabled={detailQuery.isFetching}>
            {detailQuery.isFetching ? '更新中…' : '重新整理'}
          </button>
        </div>
      </div>

      <p style={{ color: '#6b7280' }}>
        檢視某筆 P1 通知之 dispatch row、4 通道 attempts 與（若有）DLQ 項目。
      </p>

      {detailQuery.isLoading && <div>載入中…</div>}
      {detailQuery.error && (
        <div className="error-banner">{(detailQuery.error as Error).message}</div>
      )}

      {detailQuery.data && (
        <>
          <DispatchHeader detail={detailQuery.data} />
          <AttemptsTable attempts={detailQuery.data.attempts} />
          {detailQuery.data.dlq_entries && detailQuery.data.dlq_entries.length > 0 && (
            <DlqEntriesTable entries={detailQuery.data.dlq_entries} />
          )}
        </>
      )}
    </div>
  );
}
